/**
 * Becoming Tracker Component
 * 
 * A beautiful progress tracker that shows the member's 90-day journey
 * based on their chosen focus area. Includes the ability to refocus
 * when they're ready for a new chapter.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Target, Sparkles, ChevronRight, RefreshCw, 
  Flame, Heart, BookOpen, Eye, Shield, Briefcase
} from 'lucide-react';

// Focus area icons and colors
const FOCUS_CONFIG = {
  career: {
    icon: Briefcase,
    color: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200'
  },
  boundaries: {
    icon: Shield,
    color: 'from-purple-500 to-purple-600',
    bgLight: 'bg-purple-50',
    textColor: 'text-purple-600',
    borderColor: 'border-purple-200'
  },
  faith: {
    icon: Heart,
    color: 'from-pink-500 to-rose-600',
    bgLight: 'bg-pink-50',
    textColor: 'text-pink-600',
    borderColor: 'border-pink-200'
  },
  rest: {
    icon: Sparkles,
    color: 'from-teal-500 to-teal-600',
    bgLight: 'bg-teal-50',
    textColor: 'text-teal-600',
    borderColor: 'border-teal-200'
  },
  visibility: {
    icon: Eye,
    color: 'from-amber-500 to-orange-600',
    bgLight: 'bg-amber-50',
    textColor: 'text-amber-600',
    borderColor: 'border-amber-200'
  }
};

const BecomingTracker = ({ focusPath, onRefocus, className = '' }) => {
  const [showRefocusHint, setShowRefocusHint] = useState(false);
  
  // Get config for current focus area
  const config = FOCUS_CONFIG[focusPath?.focus_area] || FOCUS_CONFIG.career;
  const FocusIcon = config.icon;
  
  // Calculate milestone info
  const dayNumber = focusPath?.day_number || 1;
  const progressPercent = focusPath?.progress_percent || 0;
  const nextMilestone = dayNumber <= 30 ? 30 : dayNumber <= 60 ? 60 : 90;
  const daysToMilestone = nextMilestone - dayNumber;
  
  // Milestone labels
  const milestoneLabels = {
    30: 'Foundation',
    60: 'Momentum', 
    90: 'Transformation'
  };

  if (!focusPath) {
    return (
      <Card className={`card-base overflow-hidden ${className}`} data-testid="becoming-tracker-empty">
        <CardContent className="p-6 text-center">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-terracotta to-purple-600 flex items-center justify-center mx-auto mb-4">
            <Target className="h-8 w-8 text-white" />
          </div>
          <h3 className="font-heading text-xl font-semibold text-forest mb-2">
            Begin Your Becoming
          </h3>
          <p className="text-gray-600 mb-4">
            Set your sacred focus to start your 90-day transformation journey.
          </p>
          <Button 
            onClick={onRefocus}
            className="bg-gradient-to-r from-terracotta to-terracotta/90 hover:from-terracotta/90 hover:to-terracotta text-white"
            data-testid="set-focus-btn"
          >
            <Target className="h-4 w-4 mr-2" />
            Set Your Focus
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className={`card-base overflow-hidden ${className}`} 
      data-testid="becoming-tracker"
    >
      {/* Gradient Header */}
      <div className={`bg-gradient-to-r ${config.color} p-1`}>
        <div className="bg-white rounded-t-lg">
          <CardContent className="p-5">
            {/* Header Row */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`h-12 w-12 rounded-xl ${config.bgLight} flex items-center justify-center`}>
                  <FocusIcon className={`h-6 w-6 ${config.textColor}`} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Your Becoming</p>
                  <h3 className="font-heading text-lg font-semibold text-forest">
                    {focusPath.focus_label}
                  </h3>
                </div>
              </div>
              
              {/* Day Counter */}
              <div className="text-right">
                <p className={`text-3xl font-bold ${config.textColor}`}>{dayNumber}</p>
                <p className="text-xs text-gray-500">days in</p>
              </div>
            </div>

            {/* Progress Section */}
            <div className="space-y-3">
              {/* Progress Bar */}
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                  <span>Day 1</span>
                  <span className="font-medium">Day {nextMilestone}: {milestoneLabels[nextMilestone]}</span>
                  <span>Day 90</span>
                </div>
                <div className="relative">
                  <Progress value={progressPercent} className="h-3" />
                  {/* Milestone markers */}
                  <div className="absolute top-0 left-[33%] w-0.5 h-3 bg-white/50" />
                  <div className="absolute top-0 left-[66%] w-0.5 h-3 bg-white/50" />
                </div>
              </div>

              {/* Stats Row */}
              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-4">
                  <Badge className={`${config.bgLight} ${config.textColor} border ${config.borderColor}`}>
                    <Flame className="h-3 w-3 mr-1" />
                    {progressPercent}% Complete
                  </Badge>
                  {daysToMilestone > 0 && (
                    <span className="text-xs text-gray-500">
                      {daysToMilestone} days to {milestoneLabels[nextMilestone]}
                    </span>
                  )}
                </div>
                
                {/* Journey Link */}
                <Link 
                  to="/my-journey"
                  className="text-xs text-gray-500 hover:text-forest flex items-center gap-1 transition-colors"
                >
                  View Journey
                  <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
            </div>

            {/* Refocus Option */}
            <div 
              className="mt-4 pt-4 border-t border-gray-100"
              onMouseEnter={() => setShowRefocusHint(true)}
              onMouseLeave={() => setShowRefocusHint(false)}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600 italic">
                  "You're not behind. You're becoming."
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onRefocus}
                  className="text-gray-400 hover:text-terracotta"
                  data-testid="refocus-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refocus
                </Button>
              </div>
              {showRefocusHint && (
                <p className="text-xs text-gray-400 mt-1 animate-in fade-in duration-200">
                  Ready for a new chapter? Choose a different focus area.
                </p>
              )}
            </div>
          </CardContent>
        </div>
      </div>
    </Card>
  );
};

export default BecomingTracker;
